/**
 * ============================================================================
 * Permission 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Permission 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createPermissionApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createPermissionApi } from '@bayes-mes/shared/api/permission/factory'
 * export const permissionApi = createPermissionApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await permissionApi.getPermissionList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createPermissionApi } from '@bayes-mes/shared/api/permission/factory'
 * export const permissionApi = createPermissionApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/permission/factory
 * @description Permission 模块 API 工厂函数
 * @backend-controller backend/app/Http/Controllers/Api/V1/PermissionController.php
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法和辅助方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  Permission,                    // 权限主实体类型
  PermissionQueryParams,         // 列表查询参数
  CreatePermissionForm,          // 创建权限表单
  UpdatePermissionForm,          // 更新权限表单
  BatchCreateCrudForm,           // 批量创建CRUD权限表单
  PermissionTreeNode,            // 权限树节点类型
  RefreshUserCacheRequest,       // 刷新用户权限缓存请求
  PermissionCacheStatus,         // 权限缓存状态
  PermissionCacheRefreshResult,  // 全局缓存刷新结果
  UserPermissionCacheRefreshResult,      // 用户权限缓存刷新结果
  BatchUserPermissionCacheRefreshResult, // 批量用户权限缓存刷新结果
  SyncSuperAdminResult,          // 超级管理员权限同步结果
} from '../../types/permission'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

/**
 * 创建 Permission 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 Permission 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Permission 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 * 
 * @example
 * // Web 端使用示例
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => axios.get(url, config).then(res => res.data)
 * }
 * const permissionApi = createPermissionApi(webAdapter)
 * 
 * // App 端使用示例
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve) => {
 *     uni.request({ url, method: 'GET', success: res => resolve(res.data) })
 *   })
 * }
 * const permissionApi = createPermissionApi(uniAdapter)
 */
export function createPermissionApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 基础 CRUD 方法
    // ========================================================================

    /**
     * 获取权限列表（分页）
     * 
     * 📌 **使用场景**：
     * - 权限管理页面的列表展示
     * - 支持搜索、筛选、分页、排序
     * 
     * 📥 **请求参数**（可选）：
     * - keywords: 关键词搜索（匹配权限名称或描述）
     * - module: 模块筛选
     * - resource: 资源筛选
     * - type: 权限类型筛选（1-菜单 2-按钮 3-API 4-数据）
     * - status: 状态筛选（0-禁用 1-启用）
     * - sort_by: 排序字段（默认：sort_order）
     * - sort_order: 排序方向（asc/desc，默认：asc）
     * - per_page: 每页数量（默认：15，'all' 表示不分页）
     * - page: 页码
     * 
     * 📤 **响应数据**：
     * - items: 权限列表（Permission[]）
     * - total: 总记录数
     * - current_page: 当前页码
     * - per_page: 每页数量
     * 
     * 🔄 **数据流向**：
     * 1. 前端发送查询参数（snake_case）
     * 2. 后端 PermissionController::index() 处理
     * 3. 返回分页结果（camelCase）
     * 
     * @param params - 查询参数对象（可选）
     * @returns Promise<PaginationResult<Permission>> 分页的权限列表
     * 
     * @example
     * // 获取第一页，每页15条
     * const result = await getPermissionList({ page: 1, per_page: 15 })
     * 
     * // 搜索包含"用户"的权限
     * const result = await getPermissionList({ keywords: '用户' })
     * 
     * // 筛选API类型的权限
     * const result = await getPermissionList({ type: PermissionType.API })
     * 
     * // 获取所有权限（不分页）
     * const result = await getPermissionList({ per_page: 'all' })
     */
    getPermissionList: (params?: PermissionQueryParams) => {
      return client.get<PaginationResult<Permission>>(API_ROUTES.permission.list, { params })
    },

    /**
     * 获取权限详情
     * 
     * 📌 **使用场景**：
     * - 查看权限详细信息
     * - 编辑权限前获取当前数据
     * 
     * 📥 **请求参数**：
     * - id: 权限ID（必填）
     * 
     * 📤 **响应数据**：
     * - Permission 对象的所有字段
     * 
     * 🔄 **数据流向**：
     * 1. 前端传递权限ID
     * 2. 后端 PermissionController::show($id) 处理
     * 3. 返回完整的权限信息（camelCase）
     * 
     * @param id - 权限ID
     * @returns Promise<Permission> 权限详情
     * 
     * @example
     * const permission = await getPermissionDetail(1)
     * console.log(permission.name) // 权限名称
     */
    getPermissionDetail: (id: number | string) => {
      return client.get<Permission>(API_ROUTES.permission.detail(id))
    },

    /**
     * 创建权限
     * 
     * 📌 **使用场景**：
     * - 权限管理页面的新增功能
     * - 批量创建权限（结合 batchCreateCrudPermissions）
     * 
     * 📥 **请求参数**（CreatePermissionForm）：
     * - name: 权限名称（必填，最大100字符，唯一）
     * - module: 所属模块（可选，最大50字符）
     * - resource: 所属资源（可选，最大100字符）
     * - action: 操作动作（可选，最大20字符）
     * - description: 权限描述（可选，最大500字符）
     * - type: 权限类型（必填，1-4）
     * - http_method: HTTP方法（可选，最大20字符）
     * - http_path: HTTP路径（可选，最大255字符）
     * - status: 状态（可选，布尔值）
     * - sort_order: 排序顺序（可选，最小0）
     * 
     * 📤 **响应数据**：
     * - 创建成功后的权限完整信息（Permission）
     * 
     * 🔄 **数据流向**：
     * 1. 前端提交表单数据（snake_case）
     * 2. 后端 PermissionController::store() 验证并创建
     * 3. 记录审计日志
     * 4. 返回创建的权限信息（camelCase）
     * 
     * ⚠️ **注意**：
     * - name 字段必须唯一
     * - 字段名使用 snake_case（与后端验证规则一致）
     * 
     * @param data - 创建权限的表单数据
     * @returns Promise<Permission> 创建成功后的权限信息
     * 
     * @example
     * const newPermission = await createPermission({
     *   name: 'material:view',
     *   module: 'material',
     *   resource: 'material',
     *   action: 'view',
     *   description: '物料管理 - 查看',
     *   type: PermissionType.API,
     *   http_method: 'GET',
     *   http_path: '/api/material*',
     *   status: true,
     *   sort_order: 0
     * })
     */
    createPermission: (data: CreatePermissionForm) => {
      return client.post<Permission>(API_ROUTES.permission.create, data)
    },

    /**
     * 更新权限
     * 
     * 📌 **使用场景**：
     * - 权限管理页面的编辑功能
     * - 批量修改权限状态
     * 
     * 📥 **请求参数**：
     * - id: 权限ID（必填）
     * - data: 更新数据（UpdatePermissionForm，所有字段可选）
     * 
     * 📤 **响应数据**：
     * - 更新成功后的权限完整信息（Permission）
     * 
     * 🔄 **数据流向**：
     * 1. 前端提交权限ID和更新数据（snake_case）
     * 2. 后端 PermissionController::update($id) 验证并更新
     * 3. 记录审计日志（保存旧值和新值）
     * 4. 返回更新后的权限信息（camelCase）
     * 
     * ⚠️ **注意**：
     * - 只需传递需要更新的字段
     * - name 字段必须唯一（不包括当前记录）
     * 
     * @param id - 权限ID
     * @param data - 更新数据（所有字段可选）
     * @returns Promise<Permission> 更新后的权限信息
     * 
     * @example
     * // 只更新描述
     * const updated = await updatePermission(1, {
     *   description: '新的权限描述'
     * })
     * 
     * // 更新状态为禁用
     * const updated = await updatePermission(1, { status: false })
     */
    updatePermission: (id: number | string, data: UpdatePermissionForm) => {
      return client.put<Permission>(API_ROUTES.permission.permissions(id), data)
    },

    /**
     * 删除权限
     * 
     * 📌 **使用场景**：
     * - 权限管理页面的删除功能
     * - 清理无用的权限
     * 
     * 📥 **请求参数**：
     * - id: 权限ID（必填）
     * 
     * 📤 **响应数据**：
     * - 无（删除成功返回 void）
     * 
     * 🔄 **数据流向**：
     * 1. 前端传递权限ID
     * 2. 后端 PermissionController::destroy($id) 处理
     * 3. 检查是否有角色使用该权限
     * 4. 记录审计日志
     * 5. 删除权限
     * 
     * ⚠️ **注意**：
     * - 如果权限已分配给角色，无法删除（后端会返回错误）
     * - 删除操作不可逆，请谨慎操作
     * 
     * @param id - 权限ID
     * @returns Promise<void> 删除成功无返回值
     * 
     * @example
     * await deletePermission(1)
     * console.log('权限删除成功')
     */
    deletePermission: (id: number | string) => {
      return client.delete<void>(API_ROUTES.permission.delete(id))
    },

    // ========================================================================
    // 批量操作方法
    // ========================================================================

    /**
     * 批量创建CRUD权限
     * 
     * 📌 **使用场景**：
     * - 快速为新模块创建标准CRUD权限
     * - 简化权限配置流程
     * 
     * 📥 **请求参数**（BatchCreateCrudForm）：
     * - module: 所属模块（必填，如 'material'）
     * - resource: 所属资源（必填，如 'material'）
     * - description: 权限描述（可选，如 '物料管理'）
     * - actions: 操作列表（可选，默认 ['view', 'create', 'update', 'delete']）
     * - type: 权限类型（可选，默认为 API 类型）
     * - sort_order: 排序顺序（可选，默认 0）
     * 
     * 📤 **响应数据**：
     * - 创建的权限列表（Permission[]）
     * 
     * 🔄 **数据流向**：
     * 1. 前端提交模块和资源信息
     * 2. 后端 PermissionController::batchCreateCrud() 处理
     * 3. 调用 Permission::createCrud() 批量创建
     * 4. 记录审计日志
     * 5. 返回创建的权限列表
     * 
     * 💡 **自动生成规则**：
     * - 权限名称格式：{module}:{resource}:{action}
     * - 自动映射 HTTP 方法：view->GET, create->POST, update->PUT, delete->DELETE
     * - 自动生成权限描述：{description} - {操作中文}
     * 
     * @param data - 批量创建表单数据
     * @returns Promise<Permission[]> 创建的权限列表
     * 
     * @example
     * // 为物料管理创建标准CRUD权限
     * const permissions = await batchCreateCrudPermissions({
     *   module: 'material',
     *   resource: 'material',
     *   description: '物料管理',
     *   actions: ['view', 'create', 'update', 'delete']
     * })
     * // 返回4个权限：
     * // - material:material:view
     * // - material:material:create
     * // - material:material:update
     * // - material:material:delete
     * 
     * // 创建包含导入导出的权限
     * const permissions = await batchCreateCrudPermissions({
     *   module: 'material',
     *   resource: 'material',
     *   description: '物料管理',
     *   actions: ['view', 'create', 'update', 'delete', 'export', 'import']
     * })
     */
    batchCreateCrudPermissions: (data: BatchCreateCrudForm) => {
      return client.post<Permission[]>(API_ROUTES.permission.batchCrud, data)
    },

    // ========================================================================
    // 辅助方法
    // ========================================================================

    /**
     * 获取所有模块列表
     * 
     * 📌 **使用场景**：
     * - 权限筛选的模块下拉选择
     * - 批量创建权限时选择模块
     * 
     * 📥 **请求参数**：
     * - 无
     * 
     * 📤 **响应数据**：
     * - 模块名称列表（string[]）
     * - 去重、排序后的结果
     * 
     * 🔄 **数据流向**：
     * 1. 前端请求模块列表
     * 2. 后端 PermissionController::modules() 处理
     * 3. 从数据库查询所有不同的 module 字段
     * 4. 返回去重排序后的模块列表
     * 
     * @returns Promise<string[]> 模块名称列表
     * 
     * @example
     * const modules = await getPermissionModules()
     * // ['material', 'production', 'system', 'user', 'warehouse']
     * 
     * // 用于下拉选择
     * const options = modules.map(m => ({ label: m, value: m }))
     */
    getPermissionModules: () => {
      return client.get<string[]>(API_ROUTES.permission.modules)
    },

    /**
     * 获取权限树（Ant Design Tree 格式）
     * 
     * 📌 **使用场景**：
     * - 角色管理页面的权限分配树
     * - 用户权限展示
     * - 权限批量选择
     * 
     * 📥 **请求参数**：
     * - 无
     * 
     * 📤 **响应数据**（PermissionTreeNode[]）：
     * - key: 节点键值（模块节点：'module-{name}'，资源节点：'resource-{module}-{resource}'，权限节点：权限ID）
     * - title: 节点标题（中文）
     * - selectable: 是否可选择（权限节点为 true）
     * - children: 子节点列表
     * 
     * 🌳 **树形结构**：
     * ```
     * 模块节点（module-*）
     * └── 资源节点（resource-*）
     *     └── 权限节点（权限ID）
     * 
     * 示例：
     * - 系统管理（module-system）
     *   - 用户（resource-system-user）
     *     - 查看（1）
     *     - 创建（2）
     *     - 更新（3）
     *     - 删除（4）
     * ```
     * 
     * 🔄 **数据流向**：
     * 1. 前端请求权限树
     * 2. 后端 PermissionController::tree() 处理
     * 3. 查询所有启用的权限（sanctum guard）
     * 4. 按模块、资源分组，构建三层树形结构
     * 5. 返回 Ant Design Tree 格式的数据
     * 
     * ⚠️ **注意**：
     * - 只返回启用状态的权限（status = 1）
     * - 只返回 sanctum guard 的权限
     * - 权限节点的 key 是权限ID（用于提交选中数据）
     * 
     * @returns Promise<PermissionTreeNode[]> 权限树节点列表
     * 
     * @example
     * const tree = await getPermissionTree()
     * // 用于 Ant Design Tree 组件
     * <Tree
     *   treeData={tree}
     *   checkable
     *   onCheck={(checkedKeys) => {
     *     // checkedKeys 是选中的权限ID列表
     *     console.log('选中的权限ID:', checkedKeys)
     *   }}
     * />
     */
    getPermissionTree: () => {
      return client.get<PermissionTreeNode[]>(API_ROUTES.permission.tree)
    },

    // ========================================================================
    // 权限缓存管理方法
    // ========================================================================

    /**
     * 获取权限缓存状态
     * 
     * 📌 **使用场景**：
     * - 查看当前权限缓存状态
     * - 检查超级管理员权限是否完整
     * - 诊断权限缓存问题
     * 
     * 📤 **响应数据**（PermissionCacheStatus）：
     * - cacheDriver: 缓存驱动名称
     * - cacheKey: 缓存键名
     * - isCached: 是否已缓存
     * - totalPermissions: 权限总数
     * - totalRoles: 角色总数
     * - superAdmin: 超级管理员权限状态
     * - cacheConfig: 缓存配置信息
     * - checkedAt: 检查时间
     * 
     * @returns Promise<PermissionCacheStatus> 缓存状态信息
     */
    getCacheStatus: () => {
      return client.get<PermissionCacheStatus>(API_ROUTES.permission.cacheStatus)
    },

    /**
     * 刷新全局权限缓存
     * 
     * 📌 **使用场景**：
     * - 权限定义变更后刷新缓存
     * - 解决权限缓存不一致问题
     * - 系统维护时手动刷新
     * 
     * 📤 **响应数据**（PermissionCacheRefreshResult）：
     * - success: 是否成功
     * - durationMs: 耗时（毫秒）
     * - permissionsCount: 权限总数
     * - rolesCount: 角色总数
     * - refreshedAt: 刷新时间
     * 
     * @returns Promise<PermissionCacheRefreshResult> 刷新结果
     */
    refreshCache: () => {
      return client.post<PermissionCacheRefreshResult>(API_ROUTES.permission.cacheRefresh)
    },

    /**
     * 刷新用户权限缓存
     * 
     * 📌 **使用场景**：
     * - 用户角色变更后刷新该用户的权限缓存
     * - 解决单个用户权限不生效问题
     * - 批量刷新多个用户的权限缓存
     * 
     * 📥 **请求参数**（RefreshUserCacheRequest）：
     * - user_id: 单个用户ID（与 user_ids 二选一）
     * - user_ids: 批量用户ID列表（与 user_id 二选一）
     * 
     * 📤 **响应数据**：
     * - 单个用户：UserPermissionCacheRefreshResult
     * - 批量用户：BatchUserPermissionCacheRefreshResult
     * 
     * @param data - 请求参数
     * @returns Promise<UserPermissionCacheRefreshResult | BatchUserPermissionCacheRefreshResult>
     */
    refreshUserCache: (data: RefreshUserCacheRequest) => {
      return client.post<UserPermissionCacheRefreshResult | BatchUserPermissionCacheRefreshResult>(
        API_ROUTES.permission.cacheRefreshUser,
        data
      )
    },

    /**
     * 同步超级管理员权限
     * 
     * 📌 **使用场景**：
     * - 超级管理员权限不完整时补充缺失权限
     * - 新增权限后确保超级管理员拥有所有权限
     * - 权限系统维护
     * 
     * 📤 **响应数据**（SyncSuperAdminResult）：
     * - success: 是否成功
     * - message: 操作消息
     * - totalPermissions: 权限总数
     * - previousCount: 同步前权限数量
     * - addedCount: 新增权限数量
     * - addedPermissions: 新增的权限名称列表
     * - durationMs: 耗时（毫秒）
     * - syncedAt: 同步时间
     * 
     * @returns Promise<SyncSuperAdminResult> 同步结果
     */
    syncSuperAdmin: () => {
      return client.post<SyncSuperAdminResult>(API_ROUTES.permission.cacheSyncSuperAdmin)
    },
  }
}

/**
 * 类型导出：Permission API 方法集合的类型定义
 * 
 * 这个类型定义了 createPermissionApi() 返回对象的结构，
 * 可用于类型标注、IDE 提示等场景。
 * 
 * @example
 * // 定义一个变量来存储 API 实例
 * let permissionApi: PermissionApi
 * 
 * // 在某个初始化函数中赋值
 * function initApi(client: IRequestClient) {
 *   permissionApi = createPermissionApi(client)
 * }
 */
export type PermissionApi = ReturnType<typeof createPermissionApi>

